import { useState, useEffect, useMemo, useRef } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Settings,
  Search,
  Check,
  ChevronsUpDown,
  Plus,
  X,
  Loader2,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EditableCardActions } from './EditableCardActions';
import { useEditableCard } from '@/hooks/useEditableCard';
import { updateSchool } from '@/api/schools';
import { getWhiteLabel, createWhiteLabel } from '@/api/white-labels';
import type { SchoolEditData, SchoolOptions, UpdateSchoolData } from '@/types/school';
import type { WhiteLabel } from '@/types/white-label';

interface WhiteLabelCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
  onRefreshOptions?: () => Promise<void>;
}

interface WhiteLabelFormData {
  [key: string]: unknown;
  whiteLabelEntityId: number | null;
  whiteLabelEnvironment: 'v1' | 'v2' | null;
}

const IFRAME_WIDTH = 1440;
const IFRAME_HEIGHT = 900;

function LivePreview({ url, name }: { url: string | null; name: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !url) return;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setScale(width / IFRAME_WIDTH);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [url]);

  if (!url) return null;

  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground mb-2">Aperçu page de connexion</p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
      >
        <div
          ref={containerRef}
          className="relative rounded-lg border overflow-hidden bg-gray-100"
          style={{ aspectRatio: `${IFRAME_WIDTH} / ${IFRAME_HEIGHT}` }}
        >
          {scale > 0 && (
            <div
              className="absolute top-0 left-0 origin-top-left"
              style={{
                width: `${IFRAME_WIDTH}px`,
                height: `${IFRAME_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              <iframe
                src={url}
                title={`Aperçu ${name}`}
                className="w-full h-full border-0 pointer-events-none"
                sandbox="allow-same-origin"
                loading="lazy"
                tabIndex={-1}
              />
            </div>
          )}
          {/* Overlay to capture clicks (iframe absorbs pointer events) */}
          <div className="absolute inset-0 z-10 cursor-pointer bg-transparent group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2.5 shadow-lg">
              <ExternalLink className="h-5 w-5 text-gray-700" />
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}

export function WhiteLabelCard({ school, options, onSaved, onRefreshOptions }: WhiteLabelCardProps) {
  const [whiteLabelDetail, setWhiteLabelDetail] = useState<WhiteLabel | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Combobox state
  const [comboOpen, setComboOpen] = useState(false);
  const [comboSearch, setComboSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newWhiteLabel, setNewWhiteLabel] = useState({
    name: '',
    host: '',
    description: '',
    urlAndroid: '',
    urlIOS: '',
  });

  // V2 migration warning dialog
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

  const hasV1 = school.hasV1 ?? false;
  const hasV2 = school.hasV2 ?? false;
  const hasBothVersions = hasV1 && hasV2;

  // Determine the default environment based on school deployments
  const getDefaultEnvironment = (): 'v1' | 'v2' | null => {
    if (!hasV1 && !hasV2) return null;
    if (hasV1 && !hasV2) return 'v1';
    if (!hasV1 && hasV2) return 'v2';
    return null; // Both - user must choose
  };

  const initialData: WhiteLabelFormData = {
    whiteLabelEntityId: school.whiteLabelEntityId,
    whiteLabelEnvironment: whiteLabelDetail?.activeEnvironment ?? getDefaultEnvironment(),
  };

  const { isEditing, formData, isSaving, error, startEditing, cancelEditing, updateField, save } =
    useEditableCard<WhiteLabelFormData>({
      initialData,
      onSave: async (data) => {
        const payload: UpdateSchoolData = {
          whiteLabelEntityId: data.whiteLabelEntityId,
        };
        if (data.whiteLabelEntityId && data.whiteLabelEnvironment) {
          payload.whiteLabelEnvironment = data.whiteLabelEnvironment;
        }
        await updateSchool(school.code, payload);
      },
      onSaved: async () => {
        onSaved();
        // Refresh WL detail after save
        if (formData.whiteLabelEntityId) {
          fetchWhiteLabelDetail(formData.whiteLabelEntityId);
        } else {
          setWhiteLabelDetail(null);
        }
      },
    });

  // Intercept save to show V2 migration warning
  const handleSave = () => {
    if (
      formData.whiteLabelEntityId &&
      formData.whiteLabelEnvironment === 'v2' &&
      // Only warn if it's a new assignment or environment change
      (school.whiteLabelEntityId !== formData.whiteLabelEntityId ||
        whiteLabelDetail?.activeEnvironment !== 'v2')
    ) {
      setMigrationDialogOpen(true);
      return;
    }
    save();
  };

  const handleConfirmMigration = () => {
    setMigrationDialogOpen(false);
    save();
  };

  // Fetch full white label details for view mode
  const fetchWhiteLabelDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      const detail = await getWhiteLabel(id);
      setWhiteLabelDetail(detail);
    } catch (err) {
      console.error('Failed to fetch white label detail:', err);
      setWhiteLabelDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (school.whiteLabelEntityId) {
      fetchWhiteLabelDetail(school.whiteLabelEntityId);
    } else {
      setWhiteLabelDetail(null);
    }
  }, [school.whiteLabelEntityId]);

  const selectedWhiteLabel = useMemo(() => {
    if (!formData.whiteLabelEntityId || !options?.whiteLabels) return null;
    return options.whiteLabels.find((wl) => wl.id === formData.whiteLabelEntityId) || null;
  }, [formData.whiteLabelEntityId, options?.whiteLabels]);

  const filteredWhiteLabels = useMemo(() => {
    if (!options?.whiteLabels) return [];
    if (!comboSearch.trim()) return options.whiteLabels;
    const s = comboSearch.toLowerCase();
    return options.whiteLabels.filter((wl) => wl.name.toLowerCase().includes(s));
  }, [options?.whiteLabels, comboSearch]);

  const handleSelect = (id: number | null) => {
    updateField('whiteLabelEntityId', id);
    if (id === null) {
      updateField('whiteLabelEnvironment', null);
    } else {
      // Auto-select environment if only one version
      const env = getDefaultEnvironment();
      if (env) {
        updateField('whiteLabelEnvironment', env);
      } else {
        // Both versions - reset to let user choose
        updateField('whiteLabelEnvironment', null);
      }
    }
    setComboOpen(false);
    setComboSearch('');
  };

  const handleCreateWhiteLabel = async () => {
    setCreateError(null);

    if (!newWhiteLabel.name.trim()) {
      setCreateError('Le nom est requis');
      return;
    }
    if (!newWhiteLabel.host.trim()) {
      setCreateError('Le domaine (host) est requis');
      return;
    }

    try {
      setCreating(true);
      const result = await createWhiteLabel({
        name: newWhiteLabel.name.trim(),
        host: newWhiteLabel.host.trim(),
        description: newWhiteLabel.description.trim() || undefined,
        urlAndroid: newWhiteLabel.urlAndroid.trim() || undefined,
        urlIOS: newWhiteLabel.urlIOS.trim() || undefined,
      });

      // Refresh options to include the new white label
      if (onRefreshOptions) {
        await onRefreshOptions();
      }

      // Select the newly created white label
      updateField('whiteLabelEntityId', result.id);

      // Reset and close
      setNewWhiteLabel({ name: '', host: '', description: '', urlAndroid: '', urlIOS: '' });
      setCreateDialogOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la création';
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  // Should show environment choice?
  const showEnvironmentChoice = formData.whiteLabelEntityId !== null && hasBothVersions;

  // VIEW MODE
  if (!isEditing) {
    const wlEntity = options.whiteLabels.find((wl) => wl.id === school.whiteLabelEntityId);
    const activeEnv = whiteLabelDetail?.activeEnvironment;
    return (
      <Card>
        <CardHeader
          icon={<Settings />}
          iconColor="orange"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Marque blanche</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!wlEntity ? (
            <p className="text-sm text-muted-foreground">Aucune marque blanche configurée</p>
          ) : detailLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Chargement...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Info row */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-2">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Configuration</p>
                      <p className="text-sm">{wlEntity.name}</p>
                    </div>
                    {activeEnv && (
                      <Badge
                        variant="outline"
                        className={
                          activeEnv === 'v2'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                        }
                      >
                        {activeEnv.toUpperCase()}
                      </Badge>
                    )}
                  </div>

                  {activeEnv === 'v2' && whiteLabelDetail?.v2Host && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Domaine V2</p>
                      <a
                        href={`https://${whiteLabelDetail.v2Host}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-mono"
                      >
                        {whiteLabelDetail.v2Host}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  {activeEnv !== 'v2' && whiteLabelDetail?.v2Host && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Domaine</p>
                      <a
                        href={`https://${whiteLabelDetail.v2Host}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-mono"
                      >
                        {whiteLabelDetail.v2Host}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  {(whiteLabelDetail?.urlAndroid || whiteLabelDetail?.urlIOS) && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Applications</p>
                      <div className="flex flex-col gap-1">
                        {whiteLabelDetail.urlAndroid && (
                          <a
                            href={whiteLabelDetail.urlAndroid}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Android
                          </a>
                        )}
                        {whiteLabelDetail.urlIOS && (
                          <a
                            href={whiteLabelDetail.urlIOS}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            iOS
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Login page preview - live iframe */}
              <LivePreview
                url={whiteLabelDetail?.url
                  ? (whiteLabelDetail.url.startsWith('http') ? whiteLabelDetail.url : `https://${whiteLabelDetail.url}`)
                  : null
                }
                name={wlEntity.name}
              />
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // EDIT MODE
  return (
    <Card>
      <CardHeader
        icon={<Settings />}
        iconColor="orange"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={handleSave}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Marque blanche</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label>Configuration marque blanche</Label>

          {/* Combobox */}
          <PopoverPrimitive.Root open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={comboOpen}
                className={cn(
                  'flex min-h-12 w-full items-center justify-between',
                  'bg-muted/50 border-0 border-b-2 border-border rounded-t-lg rounded-b-none',
                  'px-4 pt-3 pb-2 text-sm',
                  'hover:bg-muted/70',
                  'focus:bg-muted/70 focus:border-b-primary focus:outline-none',
                )}
              >
                <span className={selectedWhiteLabel ? '' : 'text-muted-foreground/60'}>
                  {selectedWhiteLabel ? selectedWhiteLabel.name : 'Aucune marque blanche'}
                </span>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </button>
            </PopoverPrimitive.Trigger>

            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align="start"
                sideOffset={4}
                className={cn(
                  'z-50 w-[var(--radix-popover-trigger-width)] rounded-lg border bg-popover shadow-md',
                  'data-[state=open]:animate-in data-[state=closed]:animate-out',
                  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                )}
                onOpenAutoFocus={(e) => {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }}
              >
                {/* Search */}
                <div className="flex items-center border-b px-3">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input
                    ref={searchInputRef}
                    value={comboSearch}
                    onChange={(e) => setComboSearch(e.target.value)}
                    placeholder="Rechercher une marque blanche..."
                    className="flex h-10 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground/60"
                  />
                  {comboSearch && (
                    <X
                      className="h-4 w-4 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                      onClick={() => setComboSearch('')}
                    />
                  )}
                </div>

                {/* Options */}
                <div className="max-h-[300px] overflow-y-auto p-1">
                  {/* "Aucune" option */}
                  <button
                    type="button"
                    onClick={() => handleSelect(null)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
                      'hover:bg-muted/50',
                      !formData.whiteLabelEntityId && 'bg-primary/5',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                        !formData.whiteLabelEntityId
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground/30',
                      )}
                    >
                      {!formData.whiteLabelEntityId && <Check className="h-3 w-3" />}
                    </div>
                    <span className="text-muted-foreground">Aucune</span>
                  </button>

                  {filteredWhiteLabels.length === 0 && comboSearch.trim() ? (
                    <p className="py-4 text-center text-sm text-muted-foreground">
                      Aucun résultat
                    </p>
                  ) : (
                    filteredWhiteLabels.map((wl) => {
                      const isSelected = formData.whiteLabelEntityId === wl.id;
                      return (
                        <button
                          key={wl.id}
                          type="button"
                          onClick={() => handleSelect(wl.id)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
                            'hover:bg-muted/50',
                            isSelected && 'bg-primary/5',
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              isSelected
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground/30',
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <span className="font-medium">{wl.name}</span>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Create button */}
                <div className="border-t p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setComboOpen(false);
                      setCreateDialogOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50 text-primary"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="font-medium">Créer une nouvelle marque blanche</span>
                  </button>
                </div>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </div>

        {/* Environment choice - only when a WL is selected AND school has both V1 and V2 */}
        {showEnvironmentChoice && (
          <div className="space-y-2">
            <Label>Environnement</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => updateField('whiteLabelEnvironment', 'v1')}
                className={cn(
                  'flex-1 flex items-center gap-2 rounded-lg border-2 p-3 text-sm transition-colors',
                  formData.whiteLabelEnvironment === 'v1'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50',
                )}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    formData.whiteLabelEnvironment === 'v1'
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/30',
                  )}
                >
                  {formData.whiteLabelEnvironment === 'v1' && <Check className="h-3 w-3" />}
                </div>
                <div>
                  <span className="font-medium">V1</span>
                  <span className="text-muted-foreground ml-1">(legacy)</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => updateField('whiteLabelEnvironment', 'v2')}
                className={cn(
                  'flex-1 flex items-center gap-2 rounded-lg border-2 p-3 text-sm transition-colors',
                  formData.whiteLabelEnvironment === 'v2'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50',
                )}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                    formData.whiteLabelEnvironment === 'v2'
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/30',
                  )}
                >
                  {formData.whiteLabelEnvironment === 'v2' && <Check className="h-3 w-3" />}
                </div>
                <div>
                  <span className="font-medium">V2</span>
                  <span className="text-muted-foreground ml-1">(tenant group)</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Auto-selected environment info for single-version schools */}
        {formData.whiteLabelEntityId && !hasBothVersions && (hasV1 || hasV2) && (
          <p className="text-xs text-muted-foreground">
            Environnement : <strong>{hasV2 ? 'V2' : 'V1'}</strong> (auto-sélectionné)
          </p>
        )}
      </CardContent>

      {/* Create White Label Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle marque blanche</DialogTitle>
            <DialogDescription>
              Créer une marque blanche avec son tenant group V2.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {createError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {createError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="wl-name">Nom *</Label>
              <Input
                id="wl-name"
                value={newWhiteLabel.name}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, name: e.target.value })}
                placeholder="Ex: Mon École Premium"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-host">Domaine (Host) *</Label>
              <Input
                id="wl-host"
                value={newWhiteLabel.host}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, host: e.target.value })}
                placeholder="Ex: mon-ecole.ecole-futee.com"
              />
              <p className="text-xs text-muted-foreground">
                Le domaine du tenant group V2 (sans https://)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-description">Description</Label>
              <Input
                id="wl-description"
                value={newWhiteLabel.description}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, description: e.target.value })}
                placeholder="Description optionnelle..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-urlAndroid">URL Application Android</Label>
              <Input
                id="wl-urlAndroid"
                type="url"
                value={newWhiteLabel.urlAndroid}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, urlAndroid: e.target.value })}
                placeholder="https://play.google.com/store/apps/..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-urlIOS">URL Application iOS</Label>
              <Input
                id="wl-urlIOS"
                type="url"
                value={newWhiteLabel.urlIOS}
                onChange={(e) => setNewWhiteLabel({ ...newWhiteLabel, urlIOS: e.target.value })}
                placeholder="https://apps.apple.com/..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleCreateWhiteLabel}
              disabled={creating}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V2 Migration Warning Dialog */}
      <Dialog open={migrationDialogOpen} onOpenChange={setMigrationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle>Migration vers la marque blanche V2</DialogTitle>
            </div>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  En activant cette marque blanche sur l'environnement V2,{' '}
                  <strong>tous les utilisateurs</strong> (parents, enseignants, directeurs) de cette
                  école devront obligatoirement se connecter via le domaine de la marque blanche
                  {whiteLabelDetail?.v2Host && (
                    <>
                      {' : '}
                      <strong className="font-mono">{whiteLabelDetail.v2Host}</strong>
                    </>
                  )}
                  .
                </p>
                <p>
                  Ils ne pourront plus se connecter via l'URL par défaut École Futée.
                </p>
                <p className="text-amber-700 font-medium">
                  Cette opération déclenche une migration des comptes utilisateurs vers le nouveau
                  tenant group.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMigrationDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmMigration}
            >
              Confirmer la migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
